/**
 * FoodPulseProductDetail — single product profile assembled from a scan.
 *
 * Route:   /food-pulse/products/:productId
 * Backend: GET /api/label-scan/products/:productId
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle, ArrowLeft, Camera, Loader2, Package, ExternalLink,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;
const CREAM = "#FBF7F0";

export default function FoodPulseProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/label-scan/products/${productId}`);
        if (cancelled) return;
        setProduct(res.data);
        document.title = `${res.data.product_name} — Food Pulse`;

        // Similar products: same brand OR sharing >= 2 matched ingredients.
        const slugs = res.data.matched_slugs || [];
        const brand = res.data.brand;
        const searches = [];
        if (brand) {
          searches.push(axios.get(`${API}/api/label-scan/products`, { params: { brand, limit: 12 } }));
        }
        if (slugs.length) {
          searches.push(axios.get(`${API}/api/label-scan/products`, { params: { q: slugs[0].replace(/-/g, " "), limit: 12 } }));
        }
        if (searches.length) {
          const results = await Promise.all(searches);
          const merged = [];
          const seen = new Set([productId]);
          for (const r of results) {
            for (const p of (r.data.items || [])) {
              if (seen.has(p.id)) continue;
              seen.add(p.id);
              merged.push(p);
            }
          }
          if (!cancelled) setSimilar(merged.slice(0, 6));
        }
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <div className="text-sm opacity-70 flex items-center gap-2" data-testid="fp-product-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading product…
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen p-8" style={{ background: CREAM }}>
        <div className="max-w-3xl mx-auto rounded-2xl border p-10 text-center bg-white"
             style={{ borderColor: "#C79A3F" }} data-testid="fp-product-not-found">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-40" style={{ color: "#8B5E10" }} />
          <div className="font-display text-lg mb-1">Product not found.</div>
          <Link to="/food-pulse/products" className="text-sm underline" style={{ color: "#8B5E10" }}>
            ← Back to catalogue
          </Link>
        </div>
      </div>
    );
  }

  const ingredients = product.ingredients || [];
  const matched = new Set(product.matched_slugs || []);
  const allergens = product.allergen_warnings || [];
  const nutrition = product.nutrition || {};
  const nutritionRows = [
    ["Serving size",  nutrition.serving_size],
    ["Calories",      nutrition.calories],
    ["Total fat",     nutrition.total_fat],
    ["Saturated fat", nutrition.saturated_fat],
    ["Sodium",        nutrition.sodium],
    ["Total carb",    nutrition.total_carb],
    ["Fiber",         nutrition.dietary_fiber],
    ["Sugars",        nutrition.sugars],
    ["Added sugars",  nutrition.added_sugars],
    ["Protein",       nutrition.protein],
  ].filter(([, v]) => v);

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="fp-product-detail">
      <SeoHead section="food-pulse"
               title={`${product.product_name} — Food Pulse`}
               description={`Ingredients, nutrition and allergen information for ${product.product_name}${product.brand ? ` by ${product.brand}` : ""}.`} />

      {/* Header */}
      <section className="border-b" style={{ background: "#F0E2C7", borderColor: "#E9D8B4" }}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/food-pulse/products"
                className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-4"
                data-testid="fp-product-back">
            <ArrowLeft className="w-3 h-3" /> All products
          </Link>
          <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-none"
                 style={{ background: "#fff", border: "1px solid #C79A3F" }}>
              <Package className="w-8 h-8" style={{ color: "#8B5E10" }} />
            </div>
            <div className="flex-1">
              {product.brand && (
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">
                  {product.brand}
                </div>
              )}
              <h1 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: "#3E2A0A" }}
                  data-testid="fp-product-name">
                {product.product_name}
              </h1>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.scan_count > 1 && (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full"
                        style={{ background: "#fff", border: "1px solid #C79A3F", color: "#8B5E10" }}
                        data-testid="fp-product-scan-count">
                    {product.scan_count}× scanned by the community
                  </span>
                )}
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: "#fff", border: "1px solid #C79A3F", color: "#8B5E10" }}>
                  Confidence: {product.confidence || "medium"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {allergens.length > 0 && (
          <div className="rounded-2xl border p-5 bg-white" style={{ borderColor: "#F0C0C0" }}
               data-testid="fp-product-allergens">
            <div className="font-display text-lg mb-3 inline-flex items-center gap-2"
                 style={{ color: "#7A3E00" }}>
              <AlertTriangle className="w-4 h-4" /> Allergen alerts
            </div>
            <div className="flex flex-wrap gap-1.5">
              {allergens.map((a) => (
                <span key={a} className="text-xs px-3 py-1 rounded-full"
                      style={{ background: "#FEE7DA", color: "#7A3E00" }}>{a}</span>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }}
             data-testid="fp-product-ingredients">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg" style={{ color: "#3E2A0A" }}>Ingredients</div>
            <div className="text-[11px] font-mono opacity-70">
              {(product.matched_slugs || []).length} of {ingredients.length} in our encyclopedia
            </div>
          </div>
          {product.ingredients_raw && (
            <p className="text-xs italic opacity-70 mb-3">"{product.ingredients_raw}"</p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {ingredients.map((raw, idx) => {
              const slug = (product.matched_slugs || [])[idx];
              const isMatched = slug && matched.has(slug);
              const label = String(raw || "").trim();
              const pill = (
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border"
                      style={isMatched
                        ? { background: "#FFF3E0", borderColor: "#EA580C", color: "#7A3E00" }
                        : { background: "#F8F4EA", borderColor: "#E9D8B4", color: "#4B3A20" }}>
                  {label}
                  {isMatched && <span aria-hidden>→</span>}
                </span>
              );
              return isMatched
                ? <Link key={idx} to={`/food-pulse/encyclopedia/${slug}`}
                        data-testid={`fp-product-ing-${slug}`}>{pill}</Link>
                : <span key={idx} data-testid={`fp-product-ing-unmatched-${idx}`}>{pill}</span>;
            })}
          </div>
        </div>

        {nutritionRows.length > 0 && (
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }}
               data-testid="fp-product-nutrition">
            <div className="font-display text-lg mb-3" style={{ color: "#3E2A0A" }}>Nutrition facts</div>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {nutritionRows.map(([k, v]) => (
                <div key={k} className="rounded-lg border p-3" style={{ borderColor: "#E9D8B4" }}>
                  <dt className="text-[10px] font-mono uppercase tracking-widest opacity-60">{k}</dt>
                  <dd className="text-sm font-semibold mt-0.5">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {similar.length > 0 && (
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }}
               data-testid="fp-product-similar">
            <div className="font-display text-lg mb-3" style={{ color: "#3E2A0A" }}>
              Similar products
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {similar.map((s) => (
                <Link key={s.id} to={`/food-pulse/products/${s.id}`}
                      className="rounded-lg border p-3 hover:shadow-sm transition-all bg-white"
                      style={{ borderColor: "#E9D8B4" }}
                      data-testid={`fp-product-similar-${s.id}`}>
                  {s.brand && <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{s.brand}</div>}
                  <div className="font-display text-sm leading-tight line-clamp-2">{s.product_name}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="text-center py-6">
          <Link to="/food-pulse/label-scan"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-white font-semibold hover:shadow-md transition-shadow"
                style={{ background: "#EA580C" }}
                data-testid="fp-product-scan-another">
            <Camera className="w-4 h-4" /> Scan another product
          </Link>
          <div className="mt-3 text-[10px] opacity-60 max-w-lg mx-auto">
            Product data captured from community label scans. Report inaccuracies
            via the <Link to="/food-pulse/about" className="underline">FoodPulse team</Link>.
          </div>
        </div>
      </section>
    </div>
  );
}
